// CoverageDonut component - Donut chart showing brand coverage

import React from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';
import Card, { CardHeader, CardTitle, CardContent } from '../ui/Card';
import { FAST_FOOD_BRANDS } from '../../utils/constants';

const CoverageDonut = ({ outlets = [], className = '' }) => {
  // Count outlets by brand
  const brandCount = {};
  outlets.forEach((outlet) => {
    const brand = outlet.brand || 'Other';
    brandCount[brand] = (brandCount[brand] || 0) + 1;
  });

  // Convert to chart data
  const data = Object.entries(brandCount)
    .map(([name, value]) => {
      const brandInfo = FAST_FOOD_BRANDS.find((b) => b.name === name);
      return {
        name,
        value,
        color: brandInfo?.color || '#64748B',
      };
    })
    .sort((a, b) => b.value - a.value)
    .slice(0, 8); // Top 8 brands

  // Add "Other" if there are more brands
  const totalShown = data.reduce((sum, d) => sum + d.value, 0);
  if (outlets.length > totalShown) {
    data.push({
      name: 'Other',
      value: outlets.length - totalShown,
      color: '#CBD5E1',
    });
  }

  if (outlets.length === 0) {
    return (
      <Card className={className}>
        <CardHeader>
          <CardTitle>Brand Distribution</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-48 flex items-center justify-center text-dark-400">
            No data available
          </div>
        </CardContent>
      </Card>
    );
  }

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const { name, value } = payload[0].payload;
      const percentage = ((value / outlets.length) * 100).toFixed(1);
      return (
        <div className="bg-white border border-dark-200 rounded-lg p-2 shadow-lg">
          <p className="font-medium text-dark-900">{name}</p>
          <p className="text-sm text-dark-500">
            {value} outlets ({percentage}%)
          </p>
        </div>
      );
    }
    return null;
  };

  const renderLegend = ({ payload }) => (
    <div className="flex flex-wrap justify-center gap-2 mt-2">
      {payload.map((entry, index) => (
        <div
          key={`legend-${index}`}
          className="flex items-center gap-1 text-xs"
        >
          <span
            className="w-2 h-2 rounded-full"
            style={{ backgroundColor: entry.color }}
          />
          <span className="text-dark-600">{entry.value}</span>
        </div>
      ))}
    </div>
  );

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle>Brand Distribution</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="h-56">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={data}
                cx="50%"
                cy="45%"
                innerRadius={50}
                outerRadius={70}
                paddingAngle={2}
                dataKey="value"
              >
                {data.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
              <Legend content={renderLegend} />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Total count */}
        <div className="text-center -mt-2">
          <p className="text-2xl font-bold text-dark-900">{outlets.length}</p>
          <p className="text-xs text-dark-500">Total Outlets</p>
        </div>
      </CardContent>
    </Card>
  );
};

export default CoverageDonut;
